import { GFABankFinancing, GFAOtherFinancing } from "@dune/types";
import { Button, ControlledInput, ControlledSelect } from "@dune/ui";
import { Plus, Trash2 } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { ControlledPriceInput } from "../../components/inputs/ControlledPriceInput";

interface GFAFinancingPlanContainerProps {
	isForm?: boolean;
}

const GFAFinancingPlanContainer = ({
	isForm = true,
}: GFAFinancingPlanContainerProps) => {
	const { control } = useFormContext();
	return (
		<div className="flex flex-col gap-4">
			{isForm && (
				<div className="flex flex-row gap-4">
					<h1 className="text-[#144C65] text-lg font-medium">
						PLAN DE FINANCEMENT
					</h1>
				</div>
			)}
			<div className="border rounded-lg p-4 flex flex-col gap-8">
				<div className="w-1/2">
					<ControlledPriceInput
						name="contributionAmount"
						control={control}
						label="Apport en fonds propres"
						iconOrText="€"
						disabled={!isForm}
					/>
				</div>
				<GFABankFinancingsContainer isForm={isForm} />
				<GFAOtherFinancingsContainer isForm={isForm} />
			</div>
		</div>
	);
};

interface GFABankFinancingsContainerProps {
	isForm?: boolean;
}

const GFABankFinancingsContainer = ({
	isForm = true,
}: GFABankFinancingsContainerProps) => {
	const { control, setValue } = useFormContext();
	const { watch } = useFormContext();
	const bankFinancings = watch("bankFinancings");
	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-row gap-4 w-[95%]">
				<div className="w-1/3">Financement bancaire</div>
				<div className="w-1/3">Taux</div>
				<div className="w-1/3">Durée (en mois)</div>
			</div>
			{bankFinancings?.map((bankFinancing: GFABankFinancing, index: number) => (
				<div
					key={bankFinancing.id}
					className="flex flex-row gap-4 items-center"
				>
					<div className="flex flex-row gap-4 w-[95%]">
						<div className="w-1/3">
							<ControlledInput
								name={`bankFinancings.${index}.amount`}
								control={control}
								type="number"
								formatThousands={true}
								iconOrText="€"
								iconOrTextPosition="right"
								disabled={!isForm}
							/>
						</div>

						<div className="w-1/3">
							<ControlledInput
								name={`bankFinancings.${index}.rate`}
								control={control}
								type="number"
								iconOrText="%"
								iconOrTextPosition="right"
								disabled={!isForm}
							/>
						</div>

						<div className="w-1/3">
							<ControlledInput
								name={`bankFinancings.${index}.durationInMonths`}
								control={control}
								type="number"
								iconOrText="mois"
								iconOrTextPosition="right"
								disabled={!isForm}
							/>
						</div>
					</div>
					{isForm && (
						<Trash2
							onClick={() => {
								setValue("bankFinancings", [
									...bankFinancings.slice(0, index),
									...bankFinancings.slice(index + 1),
								]);
							}}
						/>
					)}
				</div>
			))}
			{isForm && (
				<Button
					onClick={() => {
						setValue("bankFinancings", [
							...bankFinancings,
							{ id: bankFinancings.length + 1 },
						]);
					}}
					variant="none"
					size="none"
					className="self-start"
					type="button"
				>
					<div className="flex flex-row items-center gap-2 font-medium text-sm text-primary">
						<Plus /> Ajouter un financement bancaire
					</div>
				</Button>
			)}
		</div>
	);
};

interface GFAOtherFinancingsContainerProps {
	isForm?: boolean;
}

const GFAOtherFinancingsContainer = ({
	isForm = true,
}: GFAOtherFinancingsContainerProps) => {
	const { control, setValue } = useFormContext();
	const { watch } = useFormContext();
	const otherFinancings = watch("otherFinancings");
	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-row gap-4 w-[95%]">
				<div className="w-1/2">Autres financements</div>
				<div className="w-1/2">Montant</div>
			</div>
			{otherFinancings?.map(
				(otherFinancing: GFAOtherFinancing, index: number) => (
					<div
						key={otherFinancing.id}
						className="flex flex-row gap-4 items-center"
					>
						<div className="flex flex-row gap-4 w-[95%]">
							<div className="w-1/2">
								<ControlledSelect
									name={`otherFinancings.${index}.financingSource`}
									control={control}
									options={[
										{ label: "Crowdfunding", value: "CROWDFUNDING" },
										{ label: "Dation", value: "DATION" },
										{ label: "Autre", value: "OTHER" },
									]}
									disabled={!isForm}
								/>
							</div>
							<div className="w-1/2">
								<ControlledPriceInput
									name={`otherFinancings.${index}.amount`}
									control={control}
									iconOrText="€"
									disabled={!isForm}
								/>
							</div>
						</div>
						{isForm && (
							<Trash2
								onClick={() => {
									setValue("otherFinancings", [
										...otherFinancings.slice(0, index),
										...otherFinancings.slice(index + 1),
									]);
								}}
							/>
						)}
					</div>
				),
			)}
			{isForm && (
				<Button
					onClick={() => {
						setValue("otherFinancings", [
							...otherFinancings,
							{
								id: otherFinancings.length + 1,
								financingSource: "CROWDFUNDING",
							},
						]);
					}}
					variant="none"
					size="none"
					className="self-start"
					type="button"
				>
					<div className="flex flex-row items-center gap-2 font-medium text-sm text-primary">
						<Plus /> Ajouter un financement bancaire
					</div>
				</Button>
			)}
		</div>
	);
};

export default GFAFinancingPlanContainer;
